import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";
import imaglogo from "../Assert/Image/logo.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaUserShield, FaUserTie, FaUser, FaEye, FaEyeSlash, FaArrowRight } from "react-icons/fa";
import { RiLockPasswordFill } from "react-icons/ri";
import { MdEmail } from "react-icons/md";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState("admin"); // admin | customer | developer
  const [isLoading, setIsLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isSendingResetLink, setIsSendingResetLink] = useState(false);

  const navigate = useNavigate();

  // 🌐 API URLs
  const apiMap = {
    admin: "http://localhost:5890/login",
    customer: "http://localhost:5890",
    developer: "http://localhost:5890/developer-login",
  };

  const forgotPasswordApiMap = {
    admin: "http://localhost:5890/forgot-password",
    customer: "http://localhost:5890/customer/forgot-password",
    developer: "http://localhost:5890/developer/forgot-password",
  };

  const loginAPI = apiMap[loginType];
  const forgotPasswordAPI = forgotPasswordApiMap[loginType];

  // 🔁 Auto-login if token valid
  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const isExpired = decoded.exp * 1000 < Date.now();
        if (!isExpired) {
          if (decoded.role === "admin") navigate("/admin/dashboard");
          else if (decoded.role === "developer") navigate("/developer/dashboard");
          else navigate("/customer/dashboard");
        } else {
          localStorage.clear();
          sessionStorage.clear();
        }
      } catch (err) {
        console.error("Token decode failed:", err);
        localStorage.clear();
        sessionStorage.clear();
      }
    }
  }, [navigate]);

  // 🔐 Form Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload =
        loginType === "developer"
          ? { developerEmail: username, developerPassword: password }
          : { email: username, password };

      const response = await axios.post(loginAPI, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const { token, user, developer, message } = response.data;
      const loggedUser = user || developer;

      if (!token || !loggedUser) {
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return toast.error("Invalid response from server.");
      }

      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("token", token);
      storage.setItem("user", JSON.stringify(loggedUser));

      toast.success(message || "Login successful!");

      setTimeout(() => {
        if (loginType === "admin") navigate("/admin/dashboard");
        else if (loginType === "developer") navigate("/developer/dashboard");
        else navigate("/customer/dashboard");
      }, 1500);
    } catch (error) {
      console.error("Login failed:", error.response?.data || error.message);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      toast.error(error.response?.data?.error || "Invalid credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password submission
  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSendingResetLink(true);
    try {
      const payload = 
        loginType === "developer" 
          ? { developerEmail: forgotPasswordEmail } 
          : { email: forgotPasswordEmail };

      const response = await axios.post(forgotPasswordAPI, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(response.data.message || "Password reset link sent to your email!");
      setShowForgotPasswordModal(false);
      setForgotPasswordEmail("");
    } catch (error) {
      console.error("Forgot password failed:", error.response?.data || error.message);
      toast.error(error.response?.data?.error || "Failed to send reset link. Please try again.");
    } finally {
      setIsSendingResetLink(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ 
        background: "linear-gradient(135deg, #a8e063 0%, #f76b1c 100%)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div 
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "500px" }}
          >
            <div 
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "1rem", overflow: "hidden" }}
            >
              <div 
                className="modal-header border-0"
                style={{ 
                  background: "linear-gradient(to right, #f76b1c, #ff9500)",
                  color: "white"
                }}
              >
                <h5 className="modal-title fw-bold">Reset Password</h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setForgotPasswordEmail("");
                  }}
                  disabled={isSendingResetLink}
                />
              </div>
              <div className="modal-body py-4">
                <p className="mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <div className="form-floating mb-4">
                  <div className="input-group">
                    <span className="input-group-text bg-transparent border-end-0">
                      <MdEmail style={{ color: "#f76b1c" }} />
                    </span>
                    <input
                      type="email"
                      className="form-control border-start-0 ps-1"
                      id="floatingForgotEmail"
                      placeholder="Enter your email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      style={{ 
                        height: "58px",
                        borderColor: "#a8e063",
                        borderLeft: "none"
                      }}
                    />
                   
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setForgotPasswordEmail("");
                  }}
                  disabled={isSendingResetLink}
                  style={{ borderRadius: "50px", padding: "8px 20px" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn text-white"
                  onClick={handleForgotPassword}
                  disabled={isSendingResetLink}
                  style={{ 
                    borderRadius: "50px", 
                    padding: "8px 25px",
                    background: "linear-gradient(to right, #f76b1c, #ff9500)",
                    border: "none"
                  }}
                >
                  {isSendingResetLink ? (
                    <div className="spinner-border spinner-border-sm text-white" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div 
        className="card p-4 shadow-lg w-100"
        style={{
          maxWidth: "600px",
          borderRadius: "1.5rem",
          background: "rgba(255,255,255,0.95)",
          zIndex: 1,
          border: "none",
          borderTop: "17px solid #f76b1c",
          boxShadow: "0 10px 30px rgba(247, 107, 28, 0.3)"
        }}
      >
        <div className="text-center mb-4">
          <img 
            src={imaglogo} 
            alt="Logo" 
            style={{ 
              maxHeight: "80px",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
            }} 
            className="img-fluid mb-3" 
          />
        </div>
        
        <div className="mb-4">
          <div className="d-flex justify-content-center border-bottom" style={{ borderColor: "#f8f9fa" }}>
            {[
              { type: "admin", icon: <FaUserShield />, color: "#ff7b00" }, // Orange
              { type: "customer", icon: <FaUser />, color: "#28a745" },   // Green
              { type: "developer", icon: <FaUserTie />, color: "#6f42c1" } // Purple (for contrast)
            ].map(({ type, icon, color }) => (
              <button
                key={type}
                type="button"
                className={`btn position-relative pb-3 px-4 mx-1 ${loginType === type ? 'active-tab' : 'text-muted'}`}
                onClick={() => setLoginType(type)}
                style={{
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  border: "none",
                  background: "transparent",
                  borderBottom: loginType === type ? `3px solid ${color}` : "none",
                  transition: "all 0.2s ease",
                  color: loginType === type ? color : "#6c757d"
                }}
              >
                <span className="d-flex align-items-center gap-2">
                  {React.cloneElement(icon, {
                    style: { 
                      color: loginType === type ? color : "#adb5bd",
                      fontSize: "1.1rem",
                      transition: "all 0.2s ease"
                    }
                  })}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <form 
          onSubmit={handleSubmit}
          className={shake ? "shake" : ""}
        >
          <div className="form-floating mb-3">
            <div className="input-group">
              <span className="input-group-text bg-transparent border-end-0">
                <MdEmail style={{ color: "#f76b1c" }} />
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-1"
                id="floatingUsername"
                placeholder="Email or Mobile"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                style={{ 
                  height: "58px",
                  borderColor: "#a8e063",
                  borderLeft: "none"
                }}
              />
             
            </div>
          </div>

          <div className="form-floating mb-3 position-relative">
            <div className="input-group">
              <span className="input-group-text bg-transparent border-end-0">
                <RiLockPasswordFill style={{ color: "#f76b1c" }} />
              </span>
              <input size={"50px"}
                type={showPassword ? "text" : "password"}
                className="form-control border-start-0 ps-1"
                id="floatingPassword"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ 
                  height: "58px",
                  borderColor: "#a8e063",
                  borderLeft: "none"
                }}
              />
             
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ background: "transparent", border: "none", zIndex: 5 }}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash size={"29px"} style={{ color: "#f76b1c" }} /> : <FaEye size={"29px"} style={{ color: "#f76b1c" }} />}
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ 
                  cursor: "pointer",
                  borderColor: "#f76b1c",
                  backgroundColor: rememberMe ? "#f76b1c" : "white"
                }}
              />
              <label 
                className="form-check-label ms-2" 
                htmlFor="rememberMe"
                style={{ cursor: "pointer", color: "#495057" }}
              >
                Remember Me
              </label>
            </div>
            <button 
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="btn btn-link text-decoration-none p-0"
              style={{ color: "#f76b1c", fontWeight: "500" }}
            >
              Forgot Password?
            </button>
          </div>

    
    <div className="d-grid mb-3">
  <button
    type="submit"
    className="btn btn-lg shadow d-flex justify-content-center align-items-center"
    style={{ 
      borderRadius: "50px",
      height: "50px",
      fontWeight: "600",
      letterSpacing: "0.5px",
      background: "green",
      border: "none",
      color: "white",
      transition: "all 0.3s ease"
    }}
    disabled={isLoading}
    onMouseEnter={(e) => e.target.style.opacity = "0.9"}
    onMouseLeave={(e) => e.target.style.opacity = "1"}
  >
    {isLoading ? (
      <div className="spinner-border spinner-border-sm" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    ) : (
      <>
        SIGN IN <FaArrowRight className="ms-2" />
      </>
    )}
  </button>
</div>

        </form>
      </div>
      
      <ToastContainer 
        position="top-center" 
        autoClose={3000}
        toastStyle={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          background: "#28a745",
          color: "white"
        }}
        progressStyle={{
          background: "rgba(255,255,255,0.5)"
        }}
      />
      
      {/* Custom CSS for shake animation */}
      <style jsx>{`
        .shake {
          animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
      `}</style>
    </div>
  );
};

export default LoginForm;